import { HttpClient } from './HttpClient';
import { getApiUrl } from './UrlBuilder';

import { parseToUrlQuery, JsonPatchOrderGeneratorWrapper, ObjectPath } from 'shared/utils/http';
import { CreditsList, CreditTypeList, Credit } from 'models';
import { UserManager, HALManager, SettingManager } from 'core';
import { InvalidOperationError, HttpOperationError } from 'shared/utils/exceptions';
import { sortArray } from 'shared/utils/array';

export const HIGHLIGHTED_ID = -2;
export const FURTHER_ID = -1;

export class CreditsService {
  private httpClient: HttpClient = new HttpClient();

  public getCreditsByTypeId({ credits, creditHeadings }: CreditsList, creditTypeId: number) {
    let sortedCredits = new Array<Credit>();

    if (creditTypeId >= 0) {
      sortedCredits = credits.filter((credit) => credit.typeId === creditTypeId);
    } else if (creditTypeId === HIGHLIGHTED_ID) {
      sortedCredits = credits.filter((credit) => credit.highlighted);
    } else if (creditTypeId === FURTHER_ID) {
      const hiddenHeadings = creditHeadings
        .filter((creditHeading) => creditHeading.isHidden)
        .map((creditHeading) => creditHeading.creditType.id);

      sortedCredits = credits.filter(credit => hiddenHeadings.some(id => id === credit.typeId));
    }

    return sortedCredits.sort((left, right) => left.position > right.position ? 1 : -1);
  }

  public async getCreditById(id: string) {
    const { credits: creditsList } = await this.getCredits();

    const credit = creditsList.find((credit: Credit) => credit.id === id);

    if (credit) {
      return credit;
    } else {
      throw new InvalidOperationError('Getting credit was failed', { id });
    }
  };

  public async getCredits() {
    const urlRequest = getApiUrl(HALManager.getCreditsUrl(UserManager.userId), parseToUrlQuery({ nocache: new Date().getTime() }));
    const requestParams = {
      headers: { 'Cache-control': 'no-cache, no-store, must-revalidate' }
    };

    try {
      const { headers, object: list } = await this.httpClient.get<CreditsList>(urlRequest, requestParams);

      SettingManager.eTag = { credits: headers.get('etag') || undefined };

      return list;
    } catch (error) {
      throw new HttpOperationError(urlRequest, 'Getting credits list was failed');
    }
  };

  public async getCreditTypes() {
    const urlRequest = getApiUrl(HALManager.getCreditTypesUrl());
    try {
      const { object: creditTypes } = await this.httpClient.get<CreditTypeList>(urlRequest);

      return creditTypes;
    } catch (error) {
      throw new HttpOperationError(urlRequest, 'Getting credits types was failed');
    }
  };

  public async sortCredits(newSequence: string[], credits: Credit[], isHidden: boolean, typeId?: number) {
    const eTag = SettingManager.eTag.credits || '';

    const reorderedCredits = sortArray(credits, [
      {
        expression: (credit) => newSequence.indexOf(credit.id),
        order: 'asc',
      }
    ]);

    const patchSetting = [{
      equals: (left: Credit, right: Credit) => left.id === right.id,
      path: ObjectPath.from(['credits'])
    }];

    const patchDiff = new JsonPatchOrderGeneratorWrapper()
      .generate({ credits }, { credits: reorderedCredits }, patchSetting);

    const urlRequest = getApiUrl(HALManager.patchCreditsUrl(UserManager.userId), parseToUrlQuery({ isHidden: isHidden, creditTypeId: typeId }));

    const requestParams = {
      body: JSON.stringify(patchDiff),
      headers: { 'If-Match': eTag },
    };

    const { object: creditsList, headers } = await this.httpClient.patch<CreditsList>(urlRequest, requestParams);
    SettingManager.eTag = { credits: headers.get('etag') || undefined };

    return creditsList;
  };

  public async addCredit(credit: Credit) {
    const urlRequest = getApiUrl(HALManager.addCreditUrl(UserManager.userId));
    const requestParams = { body: JSON.stringify(credit) };

    const response = await this.httpClient.post<Credit>(urlRequest, requestParams);

    return response.object;
  };

  public async updateCredit(credit: Credit) {
    const urlRequest = getApiUrl(HALManager.updateCreditUrl(UserManager.userId, credit.id));

    const requestParams = { body: JSON.stringify(credit) };

    const response = await this.httpClient.put<Credit>(urlRequest, requestParams);

    return response.object
  };

  public async deleteCredit(creditId: string) {
    const urlRequest = getApiUrl(HALManager.deleteCreditUrl(UserManager.userId, creditId));

    await this.httpClient.delete(urlRequest);
  }
}

export default new CreditsService();
